import React, { useEffect, useState, memo, useCallback, useMemo } from "react";
import { addIcon } from "../../../../../assets/Icons";
import { Edit, Delete } from '@mui/icons-material';
import AddThresholdModal from "./AddThresholdModal";
import { useDispatch } from "react-redux";
import { savePatientVitalThreshold } from "../../../../../redux/reducers/patientVitalThresholdReducer";
import { useLocation } from "react-router-dom";
import withToast from "../../../../../hocs/withToast";

const THRESHOLD_TYPES = ['normal', 'borderline', 'high', 'critical'];

const ThresholdCard = ({
    title,
    code,
    unit,
    data,
    hasSections = false,
    notify
}) => {

    const [isCustom, setIsCustom] = useState(data?.isCustom || false);
    const [showThresholdModal, setShowThresholdModal] = useState(false);
    const [thresholds, setThresholds] = useState(data?.thresholds || []);
    const [editValues, setEditValues] = useState({});
    const [isEdited, setIsEdited] = useState(false);
    const [areThresholdsComplete, setAreThresholdsComplete] = useState(false);

    const dispatch = useDispatch();
    const location = useLocation();
    const { patientId } = location.state || {};

    useEffect(() => {
        setIsCustom(data?.isCustom);
        setThresholds(data?.thresholds || []);
        setIsEdited(false);
    }, [data]);

    const hasAllThresholdTypes = useCallback((thresholdsArray) => {
        if (!thresholdsArray || !Array.isArray(thresholdsArray) || thresholdsArray.length < THRESHOLD_TYPES.length) {
            return false;
        }

        const existingTypes = thresholdsArray.map(t => t.type?.toLowerCase());
        return THRESHOLD_TYPES.every(type => existingTypes.includes(type.toLowerCase()));
    }, []);

    // Track changes to mark card as edited and check if thresholds are complete
    useEffect(() => {
        // Check if any values changed from the original data

        const defaultChanged = isCustom !== data?.isCustom;

        // For thresholds, we'd need deep comparison - simplified for now
        let thresholdsChanged = JSON.stringify(thresholds) !== JSON.stringify(data?.thresholds || []);
        if (thresholdsChanged)
            thresholdsChanged = true;

        // Check if all required threshold types exist
        const allThresholdsPresent = hasAllThresholdTypes(thresholds);
        setAreThresholdsComplete(allThresholdsPresent);

        setIsEdited(defaultChanged || thresholdsChanged);
        console.log(defaultChanged || thresholdsChanged)
    }, [isCustom, thresholds, data, hasAllThresholdTypes]);

    // Get thresholds for a specific section (for BP which has SYSTOLIC and DIASTOLIC)
    const getSectionThresholds = useCallback((sectionName) => {
        console.log(sectionName, 'sectionName')
        if (!thresholds || !Array.isArray(thresholds)) return [];
        if (!hasSections) return thresholds;

        return thresholds.filter(threshold =>
            threshold.section?.toLowerCase() === sectionName?.toLowerCase()
        );
    }, [thresholds, hasSections]);

    // Get existing threshold types for a section
    const getExistingTypes = useCallback((sectionName = null) => {
        const relevantThresholds = sectionName
            ? getSectionThresholds(sectionName)
            : thresholds;

        return relevantThresholds
            .filter(t => t.type)
            .map(t => t.type.toLowerCase());
    }, [thresholds, getSectionThresholds]);

    const handleDefaultToggle = useCallback(() => {
        setIsCustom(prev => !prev);
    }, []);

    const handleThresholdClick = useCallback((values = {}, section = null) => {
        // Get existing types for this section
        const existingTypes = getExistingTypes(section);

        const updatedValues = {
            ...values,
            vital: title,
            // Add list of existing types to disable in the modal
            existingTypes: existingTypes
        };

        // Add section info if provided (for BP)
        if (section) {
            updatedValues.section = section;
        }

        setEditValues(updatedValues);
        setShowThresholdModal(true);
    }, [title, getExistingTypes]);

    // Handle adding or updating a threshold from the modal
    const handleAddUpdateThreshold = useCallback((newThresholdData) => {
        console.log("Received threshold data from modal:", newThresholdData, code);

        const currentThresholds = Array.isArray(thresholds) ? [...thresholds] : [];

        const processedData = {
            ...newThresholdData,
            min: parseInt(newThresholdData.min, 10) || 0,
            max: parseInt(newThresholdData.max, 10) || 0,
            type: newThresholdData.type?.toLowerCase() || 'normal',
            section: newThresholdData.section
        };

        const exists = currentThresholds.some(t =>
            t.type === processedData.type &&
            (!hasSections || t.section === processedData.section)
        );

        if (exists) {
            notify(`${processedData.type} threshold already exists${processedData.section ? ` for ${processedData.section}` : ''}.`, 'error');
            return;
        }

        const isOverlapping = currentThresholds.some(t => {
            const sameSection = !hasSections || t.section === processedData.section;
            const differentType = t.type !== processedData.type;

            if (!sameSection || !differentType) return false;

            const existingMin = parseInt(t.min, 10) || 0;
            const existingMax = parseInt(t.max, 10) || 0;

            const newMin = processedData.min;
            const newMax = processedData.max;

            // Check for overlap
            return !(newMax < existingMin || newMin > existingMax);
        });

        if (isOverlapping) {
            notify(`The ${processedData.type} range (${processedData.min}-${processedData.max}) overlaps with another threshold type.`, 'error');
            return;
        }

        currentThresholds.push(processedData);
        console.log("Updated thresholds:", currentThresholds);
        setThresholds(currentThresholds);
        setIsEdited(true);
    }, [thresholds, getExistingTypes]);

    const handleDeleteThreshold = useCallback((thresholdToDelete) => {
        console.log("Deleting threshold:", thresholdToDelete);

        if (!thresholdToDelete || !thresholdToDelete.type) {
            return;
        }

        // Get the type and section for confirmation message
        const thresholdType = thresholdToDelete.type || 'selected';
        const sectionName = thresholdToDelete.section ? ` (${thresholdToDelete.section})` : '';

        // Confirm before deleting
        const confirmDelete = window.confirm(`Are you sure you want to delete the ${thresholdType} threshold${sectionName}?`);
        if (!confirmDelete) {
            return;
        }

        const currentThresholds = [...thresholds];

        console.log({ currentThresholds })

        const index = sectionName
            ? currentThresholds.findIndex(t => t.type === thresholdToDelete.type && t.section === thresholdToDelete.section)
            : currentThresholds.findIndex(t => t.type === thresholdToDelete.type);

        if (index === -1) {
            console.error("Threshold not found for deletion:", thresholdToDelete);
            return;
        }

        currentThresholds.splice(index, 1);
        console.log("Thresholds after deletion:", currentThresholds);

        setThresholds(currentThresholds);
        setIsEdited(true); // Mark as edited to show submit/cancel buttons
    }, [thresholds]);

    const handleCloseModal = useCallback(() => {
        setShowThresholdModal(false);
    }, []);

    const formatThresholdsForSubmission = useCallback(() => {
        if (!thresholds || !Array.isArray(thresholds)) return {};
        if (!hasSections) {
            const formattedData = {};
            thresholds.forEach(threshold => {
                const type = threshold.type?.toLowerCase();
                if (!type) return;

                if (!formattedData[type]) formattedData[type] = [];

                formattedData[type].push([parseInt(threshold.min) || 0, parseInt(threshold.max) || 0]);
            });

            return JSON.stringify(formattedData);
        }
        else {
            const systolicThresholds = getSectionThresholds('SYSTOLIC');
            const diastolicThresholds = getSectionThresholds('DIASTOLIC');

            const formattedSystolic = {};
            const formattedDiastolic = {};

            systolicThresholds.forEach(threshold => {
                const type = threshold.type?.toLowerCase();
                if (!type) return;

                if (!formattedSystolic[type]) formattedSystolic[type] = [];

                formattedSystolic[type].push([parseInt(threshold.min) || 0, parseInt(threshold.max) || 0]);
            });

            diastolicThresholds.forEach(threshold => {
                const type = threshold.type?.toLowerCase();
                if (!type) return;

                if (!formattedDiastolic[type]) formattedDiastolic[type] = [];

                formattedDiastolic[type].push([parseInt(threshold.min) || 0, parseInt(threshold.max) || 0]);
            });

            return JSON.stringify({
                systolic: formattedSystolic,
                diastolic: formattedDiastolic
            });
        }
    }, [thresholds, hasSections, getSectionThresholds]);

    const handleSubmit = useCallback(() => {
        const thresholdData = formatThresholdsForSubmission();
        const payload = {
            type: code,
            isCustom: isCustom,
            data: thresholdData,
            patientId
        };
        console.log('Data ready for API submission:', payload);
        dispatch(savePatientVitalThreshold(payload));
        setIsEdited(false);
    }, [isCustom, thresholds, title, formatThresholdsForSubmission]);

    const handleCancel = useCallback(() => {
        setIsCustom(data?.isCustom);
        setThresholds(data?.thresholds || []);
        setIsEdited(false);
    }, [data]);

    const renderThresholdTable = (sectionTitle = null) => {
        // Get the appropriate thresholds based on section
        const tableThresholds = sectionTitle
            ? getSectionThresholds(sectionTitle)
            : thresholds;

        // Check if this table has all four threshold types
        const hasAllTypes = hasAllThresholdTypes(tableThresholds);

        return (
            <div className="threshold-table">
                <div className="table-header">
                    <span>MIN</span>
                    <span>MAX</span>
                    <span>TYPE</span>
                </div>
                <div className="table-content">
                    {tableThresholds?.length > 0 ? (
                        tableThresholds.sort((a, b) => a.type.localeCompare(b.type)).map((threshold, index) => (
                            <div key={threshold.id || index} className="table-row">
                                <span>{threshold.min}</span>
                                <span>{threshold.max}</span>
                                <span className={`threshold-type ${threshold.type?.toLowerCase() || 'normal'}`}>
                                    {threshold.type || 'Normal'}
                                </span>
                                <div className="row-actions">
                                    <button
                                        className="action-btn edit"
                                        onClick={() => handleThresholdClick(threshold, sectionTitle)}
                                    >
                                        <span className="material-icons"><Edit /></span>
                                    </button>
                                    <button
                                        className="action-btn delete"
                                        onClick={() => handleDeleteThreshold(threshold)}
                                        title={`Delete ${threshold.type} threshold`}
                                    >
                                        <span className="material-icons"><Delete /></span>
                                    </button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="no-thresholds-message">No thresholds added yet</div>
                    )}
                </div>
                {/* Only show Add Threshold button if table doesn't have all threshold types */}
                {!hasAllTypes && (
                    <button className="add-threshold-btn dark" onClick={() => handleThresholdClick({}, sectionTitle)}>
                        <span className="add-icon"><img src={addIcon} alt='add' /></span>
                        ADD THRESHOLD
                    </button>
                )}
            </div>
        );
    };

    return (
        <div className={`threshold-card ${hasSections ? 'has-sections' : ''}`}>
            <div className="threshold-content">
                <div className="threshold-header">
                    <div className="icon-container">
                        {title === 'BLOOD GLUCOSE' && <div className="blood-glucose-icon" />}
                        {title === 'HEART RATE' && <div className="heart-rate-icon" />}
                        {title === 'BLOOD PRESSURE' && <div className="blood-pressure-icon" />}
                        {title === 'RESPIRATION RATE' && <div className="respiration-rate-icon" />}
                        {title === 'SPO2' && <div className="spo2-icon" />}
                    </div>
                    <div>
                        <div className="threshold-title">{title}</div>
                        <div className="threshold-unit">{unit}</div>
                    </div>
                </div>

                <div className="card-controls">
                    <div className="threshold-row">
                        <span>Default</span>
                        <div className="toggle-group">
                            <label className="switch dark">
                                <input type="checkbox" checked={isCustom} onChange={handleDefaultToggle} />
                                <span className="slider" />
                            </label>
                            <span className="custom-label">Custom</span>
                        </div>
                    </div>
                </div>

                {!isCustom ? (
                    <div className="default-off-message">
                        <p>Turn on Custom to view and edit thresholds</p>
                    </div>
                ) : hasSections ? (
                    <div className="threshold-sections">
                        <div className="threshold-section">
                            <h4>SYSTOLIC</h4>
                            {renderThresholdTable('SYSTOLIC')}
                        </div>
                        <div className="threshold-section">
                            <h4>DIASTOLIC</h4>
                            {renderThresholdTable('DIASTOLIC')}
                        </div>
                    </div>
                ) : (
                    renderThresholdTable()
                )}
            </div>

            <div>
                {console.log(areThresholdsComplete, isEdited, thresholds)}
                <span className="text-danger">{!areThresholdsComplete && Object.keys(thresholds)?.length ? 'Please add the values for all the type' : ''}</span>
                {/* Only show action buttons when changes are detected */}
                {isEdited && (
                    <div className="threshold-card-actions">
                        <button
                            className="submit-btn"
                            onClick={handleSubmit}
                            disabled={!areThresholdsComplete}
                            style={{ opacity: !areThresholdsComplete && Object.keys(thresholds)?.length ? 0.5 : 1 }}
                        >
                            Submit
                        </button>
                        <button
                            className="cancel-btn"
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                    </div>
                )}
            </div>

            {showThresholdModal && (
                <AddThresholdModal
                    modalOpen={showThresholdModal}
                    modalClose={handleCloseModal}
                    values={editValues}
                    disableRadio={true}
                    onSave={handleAddUpdateThreshold}
                />
            )}
        </div>
    );
};

// Memoize component to avoid unnecessary re-renders
export default withToast(memo(ThresholdCard, (prevProps, nextProps) => {
    return JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) &&
        prevProps.title === nextProps.title &&
        prevProps.unit === nextProps.unit &&
        prevProps.hasSections === nextProps.hasSections;
}));
